<template>
  <v-navigation-drawer v-model="detailview.visible" location="right" elevation="0" :width="750" temporary :scrim="false" class="detailview box-shadow-large">
    <v-skeleton-loader v-if="detailview.loading" type="heading" />

    <div v-else class="detailview-header">
      <div class="toolbar-sticky">
        <div class="toolbar-row">
          <div class="detailview-caption text-truncate">{{ detailview.title }}</div>

          <div class="toolbar-right-buttons">
            <v-menu v-if="detailview.mode === 'edit' && detailview.actions">
              <template #activator="{ props }">
                <ButtonBase iconType="more" :icon-only="true" v-bind="props" />
              </template>

              <v-card rounded="lg" class="box-shadow-large">
                <v-list>
                  <ListItem
                    v-for="(action, index) in detailview.actions(detailview.selectedItem)"
                    :key="index"
                    :title="action.title"
                    :iconType="action.iconType"
                    :color="action.color"
                    @click="action.onClick"
                  />
                </v-list>
              </v-card>
            </v-menu>

            <ButtonBase
              v-if="detailview.mode === 'create'"
              buttonText="Anlegen"
              variant="flat"
              iconType="save"
              color="primary"
              :loading="detailview.loadingSave"
              @click="detailview.onCreate"
            />

            <ButtonBase iconType="cancel" :icon-only="true" @click="detailview.performGuardedAction(() => {}, true)" />
          </div>
        </div>

        <div v-if="detailview.saveBarVisible" class="toolbar-row toolbar-actions">
          <ButtonBase buttonText="Rückgängig" iconType="undo" @click="detailview.onUndo" :disabled="detailview.loadingSave" />
          <ButtonBase buttonText="Speichern" variant="flat" iconType="save" @click="detailview.onSave" :loading="detailview.loadingSave" />
        </div>

        <v-alert v-if="detailview.saveBarError" type="error" rounded="lg" density="compact">
          <SupportAwareText :text="detailview.saveBarErrorText" />

          <!-- Aufloesbare Fehler bieten den naechsten Schritt direkt an
               (z. B. vorhandenes, archiviertes Konto uebernehmen). -->
          <div v-if="detailview.saveBarErrorAction" class="detailview-error-action">
            <ButtonBase
              :buttonText="detailview.saveBarErrorAction.label"
              variant="flat"
              iconType="save"
              :loading="detailview.saveBarErrorActionLoading"
              @click="detailview.saveBarErrorAction.handler"
            />
          </div>
        </v-alert>
      </div>
    </div>

    <v-card flat class="pa-4">
      <component :is="detailview.component" />
    </v-card>
  </v-navigation-drawer>

  <!-- Kundenreview 23.08.26, VIII: Auswahl klar benennen —
       Fortsetzen (weiter eingeben) / Verwerfen / Speichern. -->
  <YesNoDialog
    ref="refUnsavedChangesDialog"
    :title="$gt('Änderungen speichern?')"
    content="Sie haben ungespeicherte Änderungen. Möchten Sie die Eingabe fortsetzen, die Änderungen verwerfen oder speichern?"
    type="warning"
    yesButtonText="Speichern"
    yesButtonIconType="save"
    :onYes="confirmUnsavedChanges"
    noButtonText="Verwerfen"
    noButtonIconType="undo"
    :onNo="discardUnsavedChanges"
    hasCancelButton
    cancelButtonText="Fortsetzen"
    cancelButtonIconType="cancel"
    :onCancel="cancelUnsavedChanges"
  />
</template>

<script>
import { useDetailviewStore } from "@/stores/detailviewStore";
import { useAuthStore } from "@/stores/authStore.ts";

import ButtonBase from "@/components/buttons/ButtonBase.vue";
import SupportAwareText from "@/components/app/SupportAwareText.vue";
import ListItem from "@/components/utils/ListItem.vue";
import YesNoDialog from "@/components/windows/dialogs/YesNoDialog.vue";
import DetailviewComponentCompanies from "@/pages/pageOrganization/tabCompanies/detailviewComponent/Component.vue";

export default {
  name: "Detailview",

  components: {
    ButtonBase,
    ListItem,
    YesNoDialog,
    DetailviewComponentCompanies,
    SupportAwareText,
  },

  data() {
    return {
      requestedClose: false,
      pendingAction: null,
      pendingCancel: null,
    };
  },

  watch: {
    'detailview.visible'(visible) {
      if (visible) this.updateDrawerTop();
    },
    'authStore.isImpersonating'() {
      this.$nextTick(this.updateDrawerTop);
    },
  },

  setup() {
    const detailview = useDetailviewStore();
    const authStore = useAuthStore();
    return { detailview, authStore };
  },

  mounted() {
    this.detailview.performGuardedAction = this.performGuardedAction;
    this.updateDrawerTop();
    window.addEventListener("resize", this.updateDrawerTop);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updateDrawerTop);
  },

  methods: {
    async performGuardedAction(action = () => {}, closeDetailview = true) {
      if (!this.detailview.hasUnsavedChanges()) {
        if (closeDetailview) this.detailview.close();
        action();
        return;
      }

      this.requestedClose = closeDetailview;

      // Resolves true = fortfahren, false = vom Benutzer abgebrochen
      // ("Fortsetzen"). Vorher blieb das Promise bei Abbruch für immer
      // offen und blockierte jede weitere Navigation (Kundenreview
      // 23.08.26, IX: toter "Öffnen"-Button).
      return new Promise((resolve) => {
        this.pendingAction = () => {
          action();
          this.pendingAction = null;
          this.pendingCancel = null;
          resolve(true);
        };
        this.pendingCancel = () => {
          this.pendingAction = null;
          this.pendingCancel = null;
          resolve(false);
        };

        this.$refs.refUnsavedChangesDialog.openDialog();
      });
    },

    async confirmUnsavedChanges() {
      let success = false;
      if (this.detailview.mode === "edit") {
        success = await this.detailview.onSave();
      } else {
        success = await this.detailview.onCreate();
      }

      if (!success) return;

      this.finalizeGuardedAction();
    },

    async discardUnsavedChanges() {
      if (!this.requestedClose) await this.detailview.onUndo();
      this.finalizeGuardedAction();
    },

    cancelUnsavedChanges() {
      this.requestedClose = false;
      if (this.pendingCancel) this.pendingCancel();
    },

    finalizeGuardedAction() {
      if (this.requestedClose) {
        this.detailview.close();
        this.requestedClose = false;
      }

      if (this.pendingAction) {
        this.pendingAction();
        this.pendingAction = null;
      }
    },

    updateDrawerTop() {
      this.$nextTick(() => {
        const appBar = document.querySelector(".v-app-bar");
        const top = appBar ? Math.round(appBar.getBoundingClientRect().bottom) : 0;
        document.documentElement.style.setProperty("--detailview-top", `${top}px`);
      });
    },
  },
};
</script>

<style>
/* Non-scoped: das `temporary` Drawer wird von Vuetify nach <body> teleportiert,
   also außerhalb dieser Komponente. Globale Regel + CSS-Var auf :root erreichen
   es trotzdem. !important schlägt Vuetifys Inline-`top` (ohne important) und
   schiebt das Panel unter die App-Bar, damit der Schließen-Button sichtbar ist. */
.v-navigation-drawer.detailview {
  top: var(--detailview-top, 0px) !important;
  height: calc(100dvh - var(--detailview-top, 0px)) !important;
}
</style>

<style scoped>
.detailview-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid var(--border-grey-color-light);
  padding: 0;
}

.toolbar-sticky {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Die Folgeaktion sitzt unter dem Fehlertext und bleibt auch auf schmalen
   Breiten vollstaendig lesbar. */
.detailview-error-action {
  margin-top: 10px;
  display: flex;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
}

.detailview-caption {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--dark-color);
}

.toolbar-right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toolbar-actions {
  justify-content: flex-start;
  gap: 8px;
}
</style>
